/* @flow */

import React from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router'

import IfUser from '../IfUser'
import IfLoggedOut from '../IfLoggedOut'
import IfAdmin from '../IfAdmin'
import {
  isAdmin,
  hasAutomationPermissions,
  hasAnalyticsPermissions,
  hasReportPermissions,
  hasPublisherReportPermissions,
  hasRevenueReportPermissions,
  isToolUser,
} from '../../stores/usersStore'
import NavLink from './NavLink'
import CreatePoll from './CreatePoll'
import type { RootState } from '../../types'
import { selectCurrentUser } from '../../stores/currentUser'
import {
  FEATURE_ANALYTICS_PAGE,
  isFeatureEnabled,
} from '../../stores/featureFlags'

import {
  OLD_LIBRARY_URL,
  API_ADMIN_URL,
  CAMPAIGNS_URL,
  AUTOMATION_ARTICLES_URL,
  ANALYTICS_URL,
  REPORT_URL,
  PUBLISHER_REPORT_URL,
  PUBLISHER_REVENUE_URL,
  TOOL_URL,
  LIBRARY_URL,
  AUTOMATION_MATCHED_URL,
} from '../../urls'

import logo from './LOGO_white.svg'

function MainNavigation(props: {
  user: {
    id: string,
    name: string,
    email: string,
    customer?: Object,
    customerId: string,
  },
  isLoginPage: string,
  logoutButtonClick: () => void,
  isAdmin: boolean,
  isToolUser: Boolean,
  hasAutomationPermissions: boolean,
  hasAnalyticsPermissions: boolean,
  isAnalyticsPageEnabled: boolean,
  hasReportPermissions: boolean,
  hasPublisherReportPermissions: boolean,
  hasRevenueReportPermissions: boolean,
}) {
  function handleLogoutClick(e) {
    e.preventDefault()
    props.logoutButtonClick()
  }

  const {
    isLoginPage,
    isAdmin,
    isToolUser,
    hasAutomationPermissions,
    hasAnalyticsPermissions,
    isAnalyticsPageEnabled,
    hasReportPermissions,
    hasPublisherReportPermissions,
    hasRevenueReportPermissions,
  } = props

  return (
    <div
      className={`bg-dark-gray db w-100 border-box ${
        isLoginPage ? 'nav-large pv4' : 'ph5 pv3'
      } flex items-center flex-column flex-row-ns`}
    >
      {isToolUser ? (
        <a
          className={`dib flex-none ${
            isLoginPage ? 'logo-large' : 'logo-small'
          } `}
          href={LIBRARY_URL}
        >
          <img className="h-100 w-100" src={logo} alt="Opinary" />
        </a>
      ) : (
        <Link
          className={`dib flex-none ${
            isLoginPage ? 'logo-large' : 'logo-small'
          } `}
          to={'/'}
        >
          <img className="h-100 w-100" src={logo} alt="Opinary" />
        </Link>
      )}

      <IfLoggedOut>
        <div
          className="op-brand f3 work-sans bold"
          style={{ position: 'absolute', right: '6rem' }}
        >
          Opinary Dashboard
        </div>
      </IfLoggedOut>
      <ul className="flex flex-auto flex-wrap items-center list ma0">
        <IfUser>
          <li>
            {isToolUser ? (
              <a className="link f6 normal near-white ph3" href={LIBRARY_URL}>
                Library
              </a>
            ) : (
              <NavLink to={OLD_LIBRARY_URL}>Library</NavLink>
            )}
          </li>
        </IfUser>
        {(isAdmin || hasAutomationPermissions) && isToolUser && (
          <li>
            <NavLink to={AUTOMATION_MATCHED_URL}>Automation</NavLink>
          </li>
        )}
        {(isAdmin || hasAutomationPermissions) && !isToolUser && (
          <li>
            <a
              className="link f6 normal near-white ph3"
              href={AUTOMATION_ARTICLES_URL}
            >
              Automation
            </a>
          </li>
        )}
        <IfUser>
          {(hasAnalyticsPermissions || isAdmin || isAnalyticsPageEnabled) && (
            <li>
              {isToolUser ? (
                <a
                  className="link f6 normal near-white ph3"
                  href={`${TOOL_URL}/dashboard/analytics`}
                >
                  Analytics
                </a>
              ) : (
                <NavLink to={ANALYTICS_URL}>Analytics</NavLink>
              )}
            </li>
          )}
        </IfUser>
        <IfAdmin>
          <li>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={CAMPAIGNS_URL}
              className="link f6 normal near-white ph3"
            >
              Campaigns
            </a>
          </li>
        </IfAdmin>

        <IfUser>
          {hasReportPermissions && (
            <li>
              <NavLink
                to={REPORT_URL}
                className="link f6 normal near-white ph3"
              >
                Report
              </NavLink>
            </li>
          )}
        </IfUser>

        <IfUser>
          {hasPublisherReportPermissions && (
            <li>
              {isToolUser ? (
                <a
                  className="link f6 normal near-white ph3"
                  href={`${TOOL_URL}/dashboard/conversion`}
                >
                  Conversions
                </a>
              ) : (
                <NavLink
                  to={PUBLISHER_REPORT_URL}
                  className="link f6 normal near-white ph3"
                >
                  Conversions
                </NavLink>
              )}
            </li>
          )}
        </IfUser>

        <IfUser>
          {hasRevenueReportPermissions && (
            <li>
              {isToolUser ? (
                <a
                  className="link f6 normal near-white ph3"
                  href={`${TOOL_URL}/dashboard/revenue`}
                >
                  Revenue
                </a>
              ) : (
                <NavLink
                  to={PUBLISHER_REVENUE_URL}
                  className="link f6 normal near-white ph3"
                >
                  Revenue
                </NavLink>
              )}
            </li>
          )}
        </IfUser>

        <IfAdmin>
          <li>
            <a
              target="_blank"
              href={API_ADMIN_URL}
              rel="noopener noreferrer"
              className="link f6 normal near-white ph3"
            >
              Admin
            </a>
          </li>
        </IfAdmin>
        <IfUser>
          <CreatePoll />
        </IfUser>
      </ul>

      <ul className="flex list ma0">
        <IfUser>
          <li>
            <a
              className="link f6 normal near-white ph3"
              style={{ cursor: 'pointer' }}
              onClick={handleLogoutClick}
            >
              Logout
            </a>
          </li>
        </IfUser>
      </ul>
    </div>
  )
}

function mapStateToProps(state: RootState) {
  const user = selectCurrentUser(state)

  if (user) {
    return {
      user,
      isAdmin: isAdmin(user),
      isToolUser: isToolUser(user),
      hasAutomationPermissions: hasAutomationPermissions(user),
      hasAnalyticsPermissions: hasAnalyticsPermissions(user),
      hasReportPermissions: hasReportPermissions(user),
      hasPublisherReportPermissions: hasPublisherReportPermissions(user),
      hasRevenueReportPermissions: hasRevenueReportPermissions(user),
    }
  }
  return {
    isAdmin: false,
    hasAutomationPermissions: false,
    hasAnalyticsPermissions: false,
    hasReportPermissions: false,
    isAnalyticsPageEnabled: isFeatureEnabled(FEATURE_ANALYTICS_PAGE),
  }
}

export default connect(mapStateToProps)(MainNavigation)



// WEBPACK FOOTER //
// ./src/components/MainNavigation/index.js